import {
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  useRouteError,
} from 'react-router-dom'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { notFoundRoutes } from 'modules/core/routes'
import { sessionRoutes } from 'modules/core/routes/session'

import { AiReportsRoutes } from 'modules/ai_reports/routes'
import { analyticsRoutes } from 'modules/analytics/routes'
import { approvalsRoutes } from 'modules/approvals/routes'
import { atsDataRoutes } from 'modules/ats/routes'
import { atsHiringToolRoutes } from 'modules/ats_hiring_tool/routes'
import { clockInRoutes } from 'modules/attendance/routes/clockIn'
import { timeTrackingRoutes } from 'modules/attendance/routes/timeTracking'
import { authenticationRoutes } from 'modules/authentication/routes'
import { benefitsRoutes } from 'modules/benefits/routes'
import { myBenefitsRoutes } from 'modules/benefits/routes/myBenefitsRoutes'
import { billingRoutes } from 'modules/billing/routes'
import { bookkeeperRoutes } from 'modules/bookkeepers/routes'
import { calendarRoutes } from 'modules/calendar/routes'
import { companyPageConfiguratorRoutes } from 'modules/company_page_configurator/routes'
import { compensationsRoutes } from 'modules/compensations/routes'
import { complaintsRoutes } from 'modules/complaints/routes'
import { dashboardRoutes } from 'modules/dashboard/routes'
import { postDashboardRoutes } from 'modules/dashboard/routes/postsDashboard'
import { discoverRoutes } from 'modules/discover/routes'
import { filesRoutes } from 'modules/documents/routes'
import { myDocumentsRoutes } from 'modules/documents/routes/my_documents'
import { employeeLifecycleRoutes } from 'modules/employee_lifecycle/routes'
import { onboardingRoutes as employeeOnboardingRoutes } from 'modules/employee_onboarding/routes'
import { employeesRoutes } from 'modules/employees/routes'
import { engagementRoutes } from 'modules/engagement/routes'
import { financeModularRoutes } from 'modules/finance/routes/financeModularRoutes'
import { mySpendingModule } from 'modules/finance/routes/modularNavigation/mySpending'
import { insightsRoutes } from 'modules/insights/routes'
import { itManagementModuleRoutes } from 'modules/it_management/routes'
import { kudosRoutes } from 'modules/kudos/routes'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ApplicationRootPage from 'modules/layout/pages/ApplicationRootPage'
import LayoutPage from 'modules/layout/pages/LayoutPage'
import { myPayrollRoutes } from 'modules/my_payroll/routes'
import { inboxRoutes } from 'modules/notifications/routes'
import { npsRoutes } from 'modules/nps/routes'
import { pagesRoutes } from 'modules/pages/routes'
import { payrollBundleRoutes } from 'modules/payroll_bundle/routes'
import { talentAnalyticsRoutes } from 'modules/performance_analytics/routes'
import { performanceV2Routes } from 'modules/performance_v2/routes'
import { personalSettingsRoutes } from 'modules/personal_settings/routes'
import { playgroundRoutes } from 'modules/playground/routes'
import { postsRoutes } from 'modules/posts/routes'
import { processesRoutes } from 'modules/processes/routes'
import { profileRoutes } from 'modules/profile/routes'
import { projectManagementRoutes } from 'modules/project_management/routes'
import { myProjectsRoutes } from 'modules/project_management/routes/my_projects'
import { reportsRoutes } from 'modules/reports/routes'
import { salaryAdvanceRoutes } from 'modules/salary_advance/routes'
import { settingsRoutes } from 'modules/settings/routes'
import { shiftsRoutes } from 'modules/shift_management/routes'
import { spaceControlRoutes } from 'modules/space_control/routes'
import { tasksRoutes } from 'modules/tasks/routes'
import { timeOffRoutes } from 'modules/timeoff/routes'
import { trainingsRoutes } from 'modules/trainings/routes'
import { myTrainingsRoutes } from 'modules/trainings/routes/my_trainings'

// NOTE: this replaces the default error boundary from react router and will bubble up the error
//       to our global SentryErrorBoundary
const RouterErrorBoundary = () => {
  const error = useRouteError()

  throw error
}

const useApplicationRoutes = () => {
  const { navigation } = useNavigation()

  return [
    {
      path: navigation.path,
      errorElement: <RouterErrorBoundary />,
      element: <ApplicationRootPage />,
      children: [
        ...authenticationRoutes(navigation),
        ...atsHiringToolRoutes(navigation),
        ...bookkeeperRoutes(navigation),
        ...companyPageConfiguratorRoutes(navigation),
        {
          path: navigation.path, // NOTE: We don't need to lazy load the layout because it is the main route
          element: <LayoutPage />,
          children: [
            {
              element: (
                <ResponsivePage>
                  <Outlet />
                </ResponsivePage>
              ), // This is are all the routes that can participate from new Data Router API's
              // It fallbacks to the old router so we can migrate progressively.
              // Add your data routes here before the splat.
              // https://reactrouter.com/en/main/routers/picking-a-router#data-apis
              children: [
                ...approvalsRoutes(navigation),
                ...atsDataRoutes(navigation),
                ...benefitsRoutes(navigation),
                ...myBenefitsRoutes(navigation),
                ...billingRoutes(navigation),
                ...calendarRoutes(navigation),
                ...postsRoutes(navigation),
                ...complaintsRoutes(navigation),
                ...engagementRoutes(navigation),
                ...playgroundRoutes(navigation),
                ...insightsRoutes(navigation),
                ...itManagementModuleRoutes(navigation),
                ...processesRoutes(navigation),
                ...tasksRoutes(navigation),
                ...npsRoutes(navigation),
                ...projectManagementRoutes(navigation),
                ...shiftsRoutes(navigation),
                ...performanceV2Routes(navigation),
                ...talentAnalyticsRoutes(navigation),
                ...mySpendingModule(navigation),
                ...kudosRoutes(navigation),
                ...employeeOnboardingRoutes(navigation),
                ...employeeLifecycleRoutes(navigation),
                ...financeModularRoutes(navigation),
                ...trainingsRoutes(navigation),
                ...myTrainingsRoutes(navigation),
                ...settingsRoutes(navigation),
                ...employeesRoutes(navigation),
                ...discoverRoutes(navigation),
                ...inboxRoutes(navigation),
                ...personalSettingsRoutes(navigation),
                ...spaceControlRoutes(navigation),
                ...filesRoutes(navigation),
                ...myDocumentsRoutes(navigation),
                ...timeOffRoutes(navigation),
                ...reportsRoutes(navigation),
                ...salaryAdvanceRoutes(navigation),
                ...analyticsRoutes(navigation),
                ...AiReportsRoutes(navigation),
                ...myProjectsRoutes(navigation),
                ...postDashboardRoutes(navigation),
                ...payrollBundleRoutes(navigation),
                ...compensationsRoutes(navigation),
                ...myPayrollRoutes(navigation),
                ...profileRoutes(navigation),
                ...pagesRoutes(navigation),
                ...timeTrackingRoutes(navigation),
                ...clockInRoutes(navigation),
                ...dashboardRoutes(navigation),
              ],
            },
          ],
        },
        ...sessionRoutes(navigation), // IMPORTANT: this one has to go last!
        ...notFoundRoutes(navigation),
      ],
    },
  ]
}

export const ApplicationRoutes = () => {
  const routes = useApplicationRoutes()
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const router = createBrowserRouter(routes as RouteObject[])

  return <RouterProvider router={router} />
}
